import { isArray } from 'lodash';
import { param } from 'saddlebag-url';

import { formatDateNonlocalised } from '../../../../bootstrap/localisation';
import getSelectedMedianIndicativePrice from '../data-parsing/getSelectedMedianIndicativePrice';
import { normaliseMoneyAmount } from '../presentation/normaliseMoneyAmount';

import type { DateData, StayState } from '../../selectors/types';
import type { SkyscannerCommonV1Money } from '@skyscanner-internal/michelin-web-http-client';

const HOTEL_COMBO_SOURCE = 'combined_search';
const HOTEL_COMBO_SORT_PRICE_ASC = 'sort_type_price_asc';

const stringifyChildrenAges = (
  childrenAges: number | number[] | null | undefined,
): string => {
  if (childrenAges == null) return ''; // == null checks for both null and undefined
  if (isArray(childrenAges)) return childrenAges.join(',');
  return String(childrenAges);
};

// trace_info payload is ASCII-only (currency code, hotel_id, fixed source,
// numeric price), so btoa is sufficient. btoa is globally available in
// Node 16+ and all modern browsers — no polyfill needed.
const encodeBase64 = (value: string): string => btoa(value);

const buildTraceInfo = (
  hotelId: string,
  price: SkyscannerCommonV1Money.Money,
): string | null => {
  const amount = normaliseMoneyAmount(price);
  if (amount == null || !price.currencyCode) return null;

  const payload = JSON.stringify({
    source: HOTEL_COMBO_SOURCE,
    price: amount,
    currency: price.currencyCode,
    hotel_id: hotelId,
  });

  return encodeBase64(payload);
};

const buildDayviewUrl = ({
  checkinDate,
  checkoutDate,
  dateData,
  hotelId,
  impressionId,
  price,
  skyId,
  sortByPriceAsc,
  stay,
}: {
  checkinDate: Date;
  checkoutDate: Date;
  dateData?: DateData;
  hotelId?: string;
  impressionId: string;
  price?: SkyscannerCommonV1Money.Money;
  skyId: string | number;
  sortByPriceAsc?: boolean;
  stay: StayState;
}) => {
  const selectedMedianIndicativePrice = getSelectedMedianIndicativePrice(
    checkinDate,
    checkoutDate,
    dateData,
  );

  const queryParams: Record<string, string | number> = {
    's-f_iplace': skyId,
    checkin: formatDateNonlocalised(checkinDate),
    checkout: formatDateNonlocalised(checkoutDate),
    adults: stay.adults ?? 1,
    children_ages: stringifyChildrenAges(stay.childrenAges),
    rooms: 1,
    impression_id: impressionId,
  };

  if (selectedMedianIndicativePrice) {
    queryParams.selected_median_indicative_price =
      selectedMedianIndicativePrice;
  }

  if (hotelId) {
    queryParams.upsort_hotels = hotelId;
    queryParams.source = HOTEL_COMBO_SOURCE;
    if (sortByPriceAsc) queryParams.sort = HOTEL_COMBO_SORT_PRICE_ASC;
    if (price) {
      const traceInfo = buildTraceInfo(hotelId, price);
      if (traceInfo) queryParams.trace_info = traceInfo;
    }
  }

  return `/hotels/search?${param(queryParams, true)}`;
};

export default {
  buildDayviewUrl,
};
